export type DetailsRoute = {
  key:string,
  value:string,
  valueShort?:string
}[]; 
export interface AddressResult {
  status:string,
  results:any[]
}
export interface ICalculatedValue {
  address: string,
  latitude: number,
  longitude: number,
  city: string,
  zip: string,
  zip4?: string,
  county: string,
  state: string
}

type Place = google.maps.places.PlaceResult;
interface IAddressZip {
  zip: string,
  zip4?: string
}



export const calculateValues = (place:Place, country:string, allowedZip4:boolean = false) => {
  let v:any = {};
  v.address = place.name ?? place.formatted_address ?? '';
  v.latitude = place.geometry?.location?.lat?.() ?? 0;
  v.longitude = place.geometry?.location?.lng?.() ?? 0;
  const detailsRoute:DetailsRoute = place.address_components?.map(c => {
    return {
      key: c.types.join(','),
      value: c.long_name,
      valueShort: c.short_name
    }
  }) ?? [];

  const zip = retrieveZip(detailsRoute, country);
  v.city = retrieveCity(detailsRoute, country);
  v.zip = zip.zip;
  if(allowedZip4) v.zip4 = zip.zip4;
  v.county = retrieveCounty(detailsRoute, country) ?? '';
  v.state = retrieveState(detailsRoute, country) ?? '';
  return v as ICalculatedValue;
}

export const retrieveCounty = (detail:DetailsRoute, country:string) => {
  let county:string | undefined = undefined;
  const countyFromName = detail.find(d => d.value.toLowerCase().includes('county'))?.value;
  const administrativeLevelOne = detail.find(d => d.key.includes('administrative_area_level_1,political'))?.value;
  const administrativeLevelTwo = detail.find(d => d.key.includes('administrative_area_level_2'))?.value;
  const sublocality = detail.find(d => d.key.includes('sublocality,sublocality_level_1'))?.value;
  const specialCountry = ['MX', 'BR'].includes(country.toUpperCase().trim());

  if(specialCountry) {
    county = sublocality;
  } else {
    county = countyFromName ?? (administrativeLevelOne ?? administrativeLevelTwo);
  }

  return county;
}

export const retrieveCity = (detail:DetailsRoute, country:string) => {
  let city:string | undefined = undefined;
  const locality = detail.find(d => d.key.includes('locality'))?.value;
  const postalTown = detail.find(d => d.key.includes('postal_town'))?.value;
  const mexicoCity = detail.find(d => d.key.includes('administrative_area_level_1,political'))?.value;
  const brazilCity = detail.find(d => d.key.includes('administrative_area_level_2,political'))?.value;
  const administrative = detail.find(d => d.key.includes('administrative_area_level_3'))?.value;
  const specialCountry = ['UK','GB'].includes(country.toUpperCase().trim());
  const specialMexicoCountry = country.toUpperCase().trim() === 'MX';
  const specialBrazilCountry = country.toUpperCase().trim() === 'BR';

  if(specialCountry) {
    city = postalTown ?? locality ?? administrative;
  } else if(specialBrazilCountry) {
    city = brazilCity;
  } else if(specialMexicoCountry) {
    city = mexicoCity;
  } else if(locality) {
    city = locality;
  } else if(administrative) {
    city = administrative;
  } else {
    city = undefined;
  }

  return city;
}

export const retrieveState = (detailsRoute:DetailsRoute, country:string) => {
  let result:string | undefined = undefined;
  if(country.toUpperCase().trim() === 'IE') return result;
  const short = ['US','CA','AU'].includes(country.toUpperCase().trim());
  const item = detailsRoute.find(d => d.key.includes('administrative_area_level_1'));
  if(!item) return result;
  return short ? item.valueShort : item.value;
}

export const retrieveZip = (detailsRoute:DetailsRoute, country:string) => {
  const countryUS = country.toUpperCase().trim() === 'US';
  let zip, zip4 = null;
  zip = detailsRoute.find(d => d.key.includes('postal_code'))?.value ?? '';
  if(countryUS) zip4 = detailsRoute.find(d => d.key.includes('postal_code_suffix'))?.value ?? '';
  return { zip, zip4 } as IAddressZip;
}